/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import * as Yup from 'yup';

const route = 'integrated-rewards/dashboard'; 
const fetchRoute = (level) => `integrated-rewards/${level}/dashboard`;
const basePath = '/integrated-rewards-feature';

const rootCss = css`
    max-width: 100%;
    .form-container { width: 100%; }
    .preview-upload-help {
        margin: 10px 0 30px 0;
        font-size: 14px;
        color: var(--Z8);
    }
    .ms-TextField-fieldGroup, .ms-Button--default:not(.ms-Pagination-pageNumber), .ms-Dropdown-title, .ms-ComboBox:after {
        height: auto;
    }
`;

const validationSchema = Yup.object().shape({
    integratedRewardsClientDashboardId: Yup.number(),
    integratedRewardsDashboardId: Yup.number(),
    cobrandId: Yup.number(),
    clientId: Yup.number(),
    rewardHeroHeader: Yup.string().nullable(),
    rewardHeroSubHeader: Yup.string().nullable(),
    sectionHeader: Yup.string().when('hasCustomDashboard', {
        is: true,
        then: Yup.string().required('Section header is required'),
        otherwise: Yup.string().nullable(),
    }),
    sectionCopy: Yup.string().when('hasCustomDashboard', {
        is: true,
        then: Yup.string().required('Section text is required'),
        otherwise: Yup.string().nullable(),
    }),
    questionText: Yup.string().nullable(),
    navLabel: Yup.string().nullable(),
    cobrandGroup: Yup.array(),
    clientGroup: Yup.array(),
    offByDefault: Yup.boolean().required('offByDefault is required'),
    isClient: Yup.boolean().required('isClient is required'),
    isCobrand: Yup.boolean().required('isCobrand is required'),
    isPublished: Yup.boolean().required('Published status is required'),
    hasCustomDashboard: Yup.boolean().required('Custom dashboard status is required'),
});

const TEXT_FIELDS = [
  "rewardHeroHeader",
  "rewardHeroSubHeader",
  "questionHeader",
  "questionText",
  "navLabel",
  "sectionHeader",
  "sectionCopy",
];

function mapInitialValues(data) {
    // Use allRewards for CTA options
    const modelCtas = (data.allRewards ?? []).map(r => ({
        integratedRewardId: r.integratedRewardId,
        rewardName: r.rewardName,
        rewardType: r.rewardType,
        providerName: r.providerName,
        isAvailable: r.isAvailable
    }));
    const rewardIds = (data.rewards ?? []).map(r => r.integratedRewardId);

    const values = data && Object.keys(data).length ? {
        integratedRewardsClientDashboardId: data.integratedRewardsClientDashboardId || 0,
        integratedRewardsDashboardId: data.integratedRewardsDashboardId || 0,
        cobrandId: data.cobrandId || 0,
        clientId: data.clientId || 0,
        rewardHeroHeader: data.rewardHeroHeader || '',
        rewardHeroSubHeader: data.rewardHeroSubHeader || '',
        sectionHeader: data.sectionHeader || '',
        sectionCopy: data.sectionCopy || '',
        questionText: data.questionText || '',
        questionHeader: data.questionHeader || '',
        navLabel: data.navLabel || '',
        offByDefault: data.offByDefault || false,
        cobrandGroup: data.cobrandGroup || [],
        clientGroup: data.clientGroup || [],
        offByDefaultArray: data.offByDefaultArray || [],
        isClient: data.integratedRewardsClientDashboardId > 0 && data.clientId > 0,
        isCobrand: data.integratedRewardsDashboardId > 0 && data.cobrandId > 0,
        allRewards: data.allRewards || [],
        ctaModel: modelCtas,
        rewardIds: rewardIds,
        originalRewardIds: rewardIds,
        editDashboard: false,
        isPublished: typeof data.isPublished === 'boolean' ? data.isPublished : false,
        hasCustomDashboard: typeof data.hasCustomDashboard === 'boolean' ? data.hasCustomDashboard : true,
    } : {
        integratedRewardsClientDashboardId: 0,
        integratedRewardsDashboardId: 0,
        cobrandId: 0,
        clientId: 0,
        rewardHeroHeader: '',
        rewardHeroSubHeader: '',
        sectionHeader: '',
        sectionCopy: '',
        questionText: '',
        questionHeader: '',
        navLabel: '',
        offByDefault: false,
        cobrandGroup: [],
        clientGroup: [],
        offByDefaultArray: [],
        clientOrCobrand: '',
        isClient: true,
        isCobrand: false,
        allRewards: [],
        ctaModel: [],
        rewardIds: [],
        originalRewardIds: [],
        editDashboard: false,
        isPublished: false,
        hasCustomDashboard: true,
    };

    TEXT_FIELDS.forEach((field) => {
        values[`${field}_original`] = values[field];
    });
    // Track original values for new fields
    values.isPublished_original = values.isPublished;
    values.hasCustomDashboard_original = values.hasCustomDashboard;

    return values;
}

const extraValidation = (values) => {
    const validationErrors = [];

    const hasClient = !!values.clientId;
    const hasCobrand = !!values.cobrandId;

    // make sure cobrand and client are not both set
    if (hasClient && hasCobrand) {
    validationErrors.push("Please select either a client or a cobrand, not both.");
    }

    // make sure rewards list is not empty
    if (!values.rewardIds || values.rewardIds.length === 0) {
    validationErrors.push("You must select at least one reward.");
    }

    // at least one change must be made/added, ie: can not select cobrand only.
    if (!hasClient && !hasCobrand) {
    validationErrors.push("Please select a client or a cobrand.");
    }

    return validationErrors
}

const successMsg = 'Integrated Rewards Updated.';

const setErrorMsg = (show, errors) => (
    show && Object.keys(errors).length > 0
        ? `Errors in: ${Object.keys(errors).join(', ')}. Check all required fields.`
        : 'Errors in form, check all required fields and try again.'
);

const buildCustomErrorMsg = (show, customMsg) => {
    if (!show) return '';
    if (customMsg) return customMsg;

    return 'Errors in form, check all required fields and try again.';
};

const unsavedMsg = 'You have unsaved changes, are you sure you want to leave?';

function hasAnyCustomText(values) {
    return TEXT_FIELDS.some((field) => {
        return values[field] !== values[`${field}_original`];
    });
}

function haveRewardsChanged(values) {
    const current = [...(values.rewardIds || [])].sort();
    const original = [...(values.originalRewardIds || [])].sort();

    if (current.length !== original.length) return true;
    return current.some((v, i) => v !== original[i]);
}

function hasMeaningfulChanges(values) {
    // Check custom text, rewards, and new fields
    const publishedChanged = typeof values.isPublished !== 'undefined' && values.isPublished !== values.isPublished_original;
    const dashboardChanged = typeof values.hasCustomDashboard !== 'undefined' && values.hasCustomDashboard !== values.hasCustomDashboard_original;
    return hasAnyCustomText(values) || haveRewardsChanged(values) || publishedChanged || dashboardChanged;
}

export {
    route,
    fetchRoute,
    basePath,
    rootCss,
    validationSchema,
    mapInitialValues,
    extraValidation,
    successMsg,
    setErrorMsg,
    buildCustomErrorMsg,
    unsavedMsg,
    hasMeaningfulChanges,
};
